import { z } from 'zod';
import { apiClient, apiErrorCode, apiErrorStatus } from '@/lib/api-client';
import {
  ExistingAccountError,
  InvalidCredentialsError,
  type AuthUser,
  type CompleteRegistrationRequest,
  type RegistrationRequest,
  type SignInInput,
} from '../../domain/entities/auth.entity';
import type { AuthDatasource } from '../repositories/auth.repository.impl';

/** A rota de cadastro desta plataforma no Primum Core (anônima ou com token). */
export const REGISTRATION_ROUTE = '/exams-capture/registration';

/**
 * O que a tela usa da resposta do `POST /auth/sign-in`. `platformProfile`
 * vem `null` para conta da Primum sem perfil **neste** scope.
 */
const SignInResponseSchema = z.object({
  account: z.object({ email: z.string().optional() }).passthrough(),
  user: z.object({ id: z.string(), name: z.string().optional() }).passthrough(),
  platformProfile: z.object({ id: z.string() }).passthrough().nullable(),
});

/** Códigos do Firebase que querem dizer "e-mail ou senha não batem". */
const INVALID_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
]);

/**
 * O Firebase, carregado só quando o login real é usado: com mocks (e nos
 * testes) ele nunca inicializa — sem config, `getAuth` lançaria.
 */
async function firebase() {
  const [{ firebaseAuth }, sdk] = await Promise.all([
    import('@/lib/firebase'),
    import('firebase/auth'),
  ]);
  return {
    auth: firebaseAuth,
    signIn: (email: string, password: string) =>
      sdk.signInWithEmailAndPassword(firebaseAuth, email, password),
    signOut: () => sdk.signOut(firebaseAuth),
  };
}

function firebaseCode(error: unknown): string | undefined {
  return typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : undefined;
}

/**
 * O login real: **a identidade é do Firebase** (o mesmo projeto de toda a
 * Primum) e **a sessão desta plataforma é do `POST /auth/sign-in`**, com o
 * token e o `x-scope-id` que o `apiClient` anexa. É a resposta dele que diz se
 * a conta tem perfil aqui (`hasProfile`).
 *
 * O cadastro é a forma anônima de `POST /exams-capture/registration`: o
 * servidor cria identidade, conta e perfil numa chamada só, e o front entra em
 * seguida com a senha que acabou de mandar.
 */
export class AuthApiDatasource implements AuthDatasource {
  async restoreSession(): Promise<AuthUser | null> {
    const fb = await firebase();
    await fb.auth.authStateReady();
    if (!fb.auth.currentUser) return null;
    try {
      return await this.startSession();
    } catch {
      // Identidade de pé sem sessão possível aqui (conta removida, acesso
      // revogado): sai, para a tela oferecer o login em vez de travar.
      await fb.signOut().catch(() => undefined);
      return null;
    }
  }

  async signIn({ email, password }: SignInInput): Promise<AuthUser> {
    const fb = await firebase();
    try {
      await fb.signIn(email, password);
    } catch (error) {
      const code = firebaseCode(error);
      if (code && INVALID_CREDENTIAL_CODES.has(code)) {
        throw new InvalidCredentialsError();
      }
      if (code === 'auth/too-many-requests') {
        throw new Error(
          'Muitas tentativas seguidas. Espere alguns minutos e tente de novo.',
        );
      }
      throw new Error('Não conseguimos entrar agora. Tente de novo.');
    }
    return this.startSessionOrSignOut();
  }

  async signUp(body: RegistrationRequest): Promise<AuthUser> {
    try {
      // Sem token, sempre: com sessão de pé (duplo clique, outra aba), o back
      // leria a chamada como a forma autenticada e recusaria e-mail e senha.
      await apiClient.post(REGISTRATION_ROUTE, body, { anonymous: true });
    } catch (error) {
      if (
        apiErrorStatus(error) === 409 ||
        apiErrorCode(error) === 'EMAIL_ALREADY_IN_USE'
      ) {
        throw new ExistingAccountError(body.email);
      }
      throw error;
    }
    // A conta nasceu com esta senha: entra direto, sem pedir de novo.
    return this.signIn({ email: body.email, password: body.password });
  }

  async completeRegistration(
    body: CompleteRegistrationRequest,
  ): Promise<AuthUser> {
    // Com o token (o `apiClient` anexa): o back amarra a conta existente,
    // cria o perfil desta plataforma e grava o formulário.
    await apiClient.post(REGISTRATION_ROUTE, body);
    return this.startSession();
  }

  async forgotPassword(email: string): Promise<void> {
    // A mesma rota dos outros apps: quem manda o e-mail é o back, com o link
    // e o template da casa. Sem sessão, de propósito.
    await apiClient.post(
      '/auth/forgot-password',
      { email },
      { anonymous: true },
    );
  }

  async signOut(): Promise<void> {
    await (await firebase()).signOut();
  }

  /** Troca a identidade do Firebase pela sessão desta plataforma. */
  private async startSession(): Promise<AuthUser> {
    const { data } = await apiClient.post<unknown>('/auth/sign-in');
    const parsed = SignInResponseSchema.parse(data);
    const fb = await firebase();
    return {
      id: parsed.user.id,
      email: parsed.account.email ?? fb.auth.currentUser?.email ?? '',
      name: parsed.user.name ?? '',
      hasProfile: parsed.platformProfile !== null,
    };
  }

  /**
   * O `startSession` depois de um login: se o Primum Core recusar, a
   * identidade do Firebase não pode ficar de pé — a pessoa ficaria "logada"
   * sem sessão nenhuma aqui.
   */
  private async startSessionOrSignOut(): Promise<AuthUser> {
    try {
      return await this.startSession();
    } catch (error) {
      await (await firebase()).signOut().catch(() => undefined);
      throw toSignInError(error);
    }
  }
}

/** Os desfechos de `/auth/sign-in` que a tela de login sabe explicar. */
function toSignInError(error: unknown): Error {
  switch (apiErrorCode(error)) {
    case 'ACCOUNT_NOT_FOUND':
      // Identidade no Firebase sem conta na Primum: para a pessoa, é o mesmo
      // que e-mail ou senha errados.
      return new InvalidCredentialsError();
    case 'PLATFORM_ACCESS_INACTIVE':
      return new Error(
        'Seu acesso a esta plataforma está desativado. Fale com o suporte.',
      );
    case 'PASSWORD_CHANGE_REQUIRED':
      return new Error(
        'Você precisa criar uma senha nova. Use "Esqueci minha senha".',
      );
    default:
      return new Error('Não conseguimos entrar agora. Tente de novo.');
  }
}
